import {Module} from "vuex";
import {IDBase, QueryFacade} from "@/controller/QueryFacade";
import {AsyncJavaRequest, Main} from "@/controller/main";
import {DieAndToolCodeRow, ImportConfigStates} from "@/store/ImportConfigStore";
import {PPanelBuildPhase, PPanelData, ppanelStore, PPanelStore, ProcuranceLine} from "@/store/PPanelStore";


interface ToolCreationSetup {
    tool: SimpleToolLine,
    component: SimpleComponentLine,
    position: number
}

class CSVLine {
    doImport: boolean = false;
    canImport: boolean = false;
    exists: boolean = false;
    data: string[] = [];

    message?: string;
    mainPart?: SimpleComponentLine;
    company?: SimpleCompanyLine;
    companyPart?: SimpleComponentLine;
    sourceShortHand?: string;
    dieSetData?: { [dieSetNumber: string]: DieSetData };
    partName?: string;

}

interface RequiredComponentCreation {
    component?: SimpleComponentLine;
    callbacks: Array<(createdComponent: SimpleComponentLine) => void>;
}

interface DieSetData {
    dieAndToolCodeRow: DieAndToolCodeRow[];
    component?: SimpleComponentLine;
}

class SimpleProjectLine {
    name: string = "";
    children: SimpleComponentLine[] = [];

}

class SimpleCompanyLine {
    firmaId?: number;
    name?: string;
    strasse?: string;
    nummer?: string;
    plz?: string;
    ort?: string;
    land?: string;
    email?: string;
    vendorId?: string;
    shorthand?: string;
    contractPersonId?: number;
}

class SimpleComponentLine {
    type: string = "ib";
    ib: string = "";
    componentId: number | undefined;
    projectId: number | undefined;
    parentId: number | undefined;
    supplierId: number | undefined;
    contractPersonId: number | undefined;
    contractCompanyId: number | undefined;
    throughputTime: number | undefined;
    componentTypeId: number | undefined;
    name: string | undefined;
    description: string | undefined;
    contractPersonFullName: string | undefined;
    partNumber: string | undefined;
    inventoryNumbers: string | undefined;
    supplierName: string | undefined;
    children: SimpleComponentLine[] = [];
    report:boolean = true;
}

class SimpleToolLine {
    werkzeugId: number | undefined;
    nummer: string | undefined;
    name: string | undefined;
    beschreibung: string | undefined;
    bauteilId: number | undefined;
    laufendenummer: number | undefined;
    istaktiv: boolean = true;
}




export class PPanelImportState {
    public existing: number = 0;
    public existingStructure: {
        projects: SimpleProjectLine[],
        companies: SimpleCompanyLine[]
    } = {projects: [], companies: []};
    public csvToImport: CSVLine[] = [];
    public started: number = Date.now();
    public stateMessage: string = "";
    public selectedProject: SimpleProjectLine | undefined;
    public importConfigStates: ImportConfigStates = new ImportConfigStates();

    public requiredComponentCreation: CSVLine[] = [];
    public componentCreations: { [partNumber: string]: RequiredComponentCreation } = {};
    public requiredToolCreation: { [componentToolKey: string]: ToolCreationSetup
    } = {};

    public companyComponentTypeId?: number;
    public componentComponentTypeId?: number;

    public createdComponents: number = 0;
    createdTools: number = 0;

    public ppanels: {[id:string]:PPanelData}={};
}

export class PPanelImportStore implements Module<any, unknown> {
    public namespaced = true;
    public state: PPanelImportState = new PPanelImportState();
    public getters: { [func: string]: ( state: PPanelImportState) => any } = {
        selectedProject(state: PPanelImportState){
            return state.selectedProject;
        }
    };
    public mutations = {
        setMessage(state: PPanelImportState, message: string) {
            state.stateMessage = message;
        },

        setCsv(state: PPanelImportState, csv: string) {
            ppanelImportStore.currentCsv = csv;
            state.csvToImport = [];
            state.started = Date.now();
            const data = csv2array(csv, ";");
            for (let datum of data) {
                let csvLine = new CSVLine();
                csvLine.data = datum;
                ppanelImportStore.analyzeImportData(csvLine);

                if (csvLine.canImport) {
                    csvLine.doImport = true;
                }

                state.csvToImport.push(csvLine);
            }

        },

        setProject(state: PPanelImportState, project: SimpleProjectLine) {
            state.selectedProject = project;
        },

        async importSelected(state: PPanelImportState) {
            const ppds: {[id:string]:PPanelData}={};
            const ppdsLoadedFromDb: {[id:string]:boolean}={};
            let importedRows=0;
            for (let csvLine of state.requiredComponentCreation) {
                if(!csvLine.doImport){
                    continue;
                }

                console.log("import",csvLine);
                const ppid = ""+csvLine.companyPart?.componentId;
                if(ppid) {
                    if (!ppds[ppid]){
                        const res = state.ppanels[ppid];
                        if(res){
                            ppds[ppid] = res;
                            ppdsLoadedFromDb[ppid]=true;
                        }
                        else{
                            ppds[ppid] = new PPanelData();
                            ppds[ppid].id = ppid;
                            ppdsLoadedFromDb[ppid]=false;
                        }
                    }


                    const pPanelData = ppds[ppid];
                    let buildPhase = csvLine.data[27];

                    if(!pPanelData.buildPhases[buildPhase]){
                        pPanelData.buildPhases[buildPhase] = new PPanelBuildPhase();
                    }
                    if(!pPanelData.buildPhases[buildPhase].dataLines){
                        pPanelData.buildPhases[buildPhase].dataLines = [];
                    }
                    pPanelData.buildPhases[buildPhase].header.MRDDate = csvLine.data[28];


                    const partNumbers = csvLine.data[18].split(",");
                    const suffixes = csvLine.data[20].split(",");
                    const cadVersions = csvLine.data[21].split(",");

                    for (let i = 0; i < partNumbers.length; i++) {
                        const aPartNumber = partNumbers[i].trim();

                        if(!suffixes[i]) {
                            alert("Inconsistent data for\n" +
                                "Master program: "+csvLine.data[0]+"\n" +
                                "Dieset number: "+csvLine.data[1]+"\n" +
                                "The amount of comma separated entries in 'Part No' \n" +
                                "did not match with amount of 'Suffix' or 'CAD Version'!\n" +
                                "\n" +
                                "Import was stopped, please fix that and check\n" +
                                "the already imported data.");
                            return;
                        }
                        if(!cadVersions[i]){
                            cadVersions[i] = "";
                        }

                        const aSuffix = suffixes[i].trim();
                        const aCadVer = cadVersions[i].trim();

                        const procuranceLine = new ProcuranceLine(pPanelData.buildPhases[buildPhase].dataLines.length);
                        pPanelData.buildPhases[buildPhase].dataLines.push(procuranceLine);

                        procuranceLine.DIESETNo = csvLine.data[1];
                        procuranceLine.PARTNo = aPartNumber;
                        procuranceLine.PARTNAME = csvLine.data[6];
                        procuranceLine.PartQuantity = Number.parseFloat(csvLine.data[29]);
                        procuranceLine.ReleaseNotice = csvLine.data[8];
                        procuranceLine.DieSetShip = csvLine.data[23];
                        procuranceLine.SFX = aSuffix;
                        procuranceLine.CADVer = aCadVer;
                        procuranceLine.AvailableAtPlant = csvLine.data[25];
                        procuranceLine.ReceivingDateAtFord = csvLine.data[28];


                        procuranceLine.StampingPlantProductionSource = csvLine.data[5];
                        importedRows++;
                    }
                }
            }

            for (let value of Object.values(ppds)) {
                await QueryFacade.put("procurance_panel", value);
            }
            alert("Import finished\n "+importedRows+" entries imported");

            return;


        },

        async loadFromDb(state: PPanelImportState) {
            const data = await Main.postMessage(new AsyncJavaRequest("call", "allComponents"));
            state.existingStructure = data;
            if (state.existingStructure.projects.length > 0) {
                state.selectedProject = (<any>state.existingStructure.projects)[0];
            }

            const orgstate = new ImportConfigStates();
            for (let key of Object.keys(orgstate)) {
                const res = await <IDBase><any>QueryFacade.get("import_config", key);
                (<any>state).importConfigStates[key] = (<any>orgstate)[key];
                if (res) {
                    (<any>state).importConfigStates[res.id] = res;
                }
            }
        }
    };
    private currentCsv: string | undefined;

    constructor() {
        for (let key of Object.keys(this.state)) {
            this.getters[key] = () => {
                return (<any>this.state)[key];
            }
        }
    }

    private async analyzeImportData(csvLine: CSVLine): Promise<void> {
        const data = csvLine.data;
        csvLine.exists = false;
        csvLine.canImport = data[0].trim() != "" &&
            !data[0].startsWith("Dieset Timing") &&
            !data[0].startsWith("Master program") &&
            !csvLine.exists;

        if (!csvLine.canImport) {
            return;
        }

        const companies = this.state.existingStructure.companies;

        for (let company of companies) {
            if (company.shorthand == data[3]) {
                csvLine.company = company;
                break;
            }
        }

        if (!csvLine.company) {
            csvLine.canImport = false;
            csvLine.message = "no company found for Construction Source '" + data[3] + "', check your companies";
            return;
        }

        const prodSource = data[4];
        for (let productionSourceRow of this.state.importConfigStates.ProductionSourceMap.map) {
            if (productionSourceRow.stampsImport == prodSource) {
                csvLine.sourceShortHand = productionSourceRow.shorthand;
            }
        }

        const toolIdsToAdd = data[5].split(",");
        const toolsToAdd: DieAndToolCodeRow[] = [];
        for (let toolid of toolIdsToAdd) {
            for (let toolRow of this.state.importConfigStates.DieAndToolCodeMap.map) {
                if (toolRow.id == toolid) {
                    toolsToAdd.push(toolRow);
                }
            }
        }

        console.log("Import-Line",csvLine)
        //check which components have to be created
        let children = this.state.selectedProject?.children;
        if (children) {
            //search for Ford Project node
            for (let child of children) {
                if (child.partNumber?.startsWith(data[0])) {
                    csvLine.mainPart = child;
                    break;
                }
            }
            //search for Company node
            if (csvLine.mainPart) {
                children = csvLine.mainPart?.children;
                if (children && csvLine.company) {
                    for (let child of children) {
                        if (
                            child.supplierId == csvLine.company.firmaId
                        ) {
                            csvLine.companyPart = child;

                            //check existing
                            const ppid = ""+csvLine.companyPart?.componentId;
                            let ppanel = this.state.ppanels[ppid];

                            if(!ppanel) {
                                const res = await <PPanelData><any>QueryFacade.get("procurance_panel", ppid);

                                if(res) {
                                    this.state.ppanels[ppid] = res;
                                    ppanel=res;
                                }
                            }

                            if(ppanel){
                                const buildPhase = csvLine.data[27];
                                if(ppanel.buildPhases[buildPhase]){
                                    csvLine.doImport = false;
                                    csvLine.message = "Build phase '" + buildPhase + "' exists already";
                                    break;
                                }
                            }
                            break;
                        }
                    }
                }

            } else {
                csvLine.canImport = false;
                csvLine.message = "Main component for " + data[0] + " must be created manually";
                return;
            }

            if(csvLine.companyPart){
                this.state.requiredComponentCreation.push(csvLine);
            }
            else {
                csvLine.doImport = false;
                csvLine.canImport = false;
                csvLine.message = "No source company found to store PPanel";
                return;
            }
        }
    }


    private createComponentInstanceForCompany(c: SimpleCompanyLine, csvLine: CSVLine, parent: SimpleComponentLine, typeId: number | undefined) :SimpleComponentLine {
        for (let child of parent.children) {
            if(child.partNumber === c.name){
                //exists already
                return child;
            }
        }

        const newComponent = new SimpleComponentLine();
        newComponent.partNumber = c.name;
        newComponent.name = c.name;
        newComponent.contractCompanyId = c.firmaId;
        newComponent.supplierId = c.firmaId;
        newComponent.contractPersonId = c.contractPersonId;
        newComponent.parentId = parent.componentId;
        newComponent.projectId = parent.projectId;
        newComponent.componentTypeId = typeId;
        newComponent.report=true;
        delete newComponent.ib;
        delete newComponent.type;
        return newComponent;
    }

    private createComponentInstance(partnumber: string, csvLine: CSVLine, parent: SimpleComponentLine, typeId: number | undefined) {
        const newComponent = new SimpleComponentLine();
        newComponent.partNumber = partnumber;
        newComponent.name = csvLine.partName;
        newComponent.contractCompanyId = csvLine.company?.firmaId;
        newComponent.contractPersonId = parent.contractPersonId;
        newComponent.supplierId = csvLine.company?.firmaId;
        newComponent.parentId = parent.componentId;
        newComponent.projectId = parent.projectId;
        newComponent.componentTypeId = typeId;
        delete newComponent.ib;
        delete newComponent.type;
        return newComponent;
    }
}


/**
 * Convert data in CSV (comma separated value) format to a javascript array.
 *
 * Values are separated by a comma, or by a custom one character delimeter.
 * Rows are separated by a new-line character.
 *
 * Leading and trailing spaces and tabs are ignored.
 * Values may optionally be enclosed by double quotes.
 * Values containing a special character (comma's, double-quotes, or new-lines)
 *   must be enclosed by double-quotes.
 * Embedded double-quotes must be represented by a pair of consecutive
 * double-quotes.
 *
 * Example usage:
 *   var csv = '"x", "y", "z"\n12.3, 2.3, 8.7\n4.5, 1.2, -5.6\n';
 *   var array = csv2array(csv);
 *
 * Author: Jos de Jong, 2010
 *
 * @param {string} data      The data in CSV format.
 * @param {string} delimeter [optional] a custom delimeter. Comma ',' by default
 *                           The Delimeter must be a single character.
 * @return {Array} array     A two dimensional array containing the data
 * @throw {String} error     The method throws an error when there is an
 *                           error in the provided data.
 */
function csv2array(data: string, delimeter: string): Array<Array<string>> {
    // Retrieve the delimeter
    if (delimeter == undefined)
        delimeter = ',';
    if (delimeter && delimeter.length > 1)
        delimeter = ',';

    // initialize variables
    var newline = '\n';
    var eof = '';
    var i = 0;
    var c = data.charAt(i);
    var row = 0;
    var col = 0;
    var array: Array<Array<string>> = [];

    while (c != eof) {
        // skip whitespaces
        while (c == ' ' || c == '\t' || c == '\r') {
            c = data.charAt(++i); // read next char
        }

        // get value
        let value: string = "";
        if (c == "\"") {
            // value enclosed by double-quotes
            c = data.charAt(++i);

            do {
                if (c != "\"") {
                    // read a regular character and go to the next character
                    value += c;
                    c = data.charAt(++i);
                }

                if (c == "\"") {
                    // check for escaped double-quote
                    var cnext = data.charAt(i + 1);
                    if (cnext == "\"") {
                        // this is an escaped double-quote.
                        // Add a double-quote to the value, and move two characters ahead.
                        value += "\"";
                        i += 2;
                        c = data.charAt(i);
                    }
                }
            }
            while (c != eof && c != "\"");

            if (c == eof) {
                throw "Unexpected end of data, double-quote expected";
            }

            c = data.charAt(++i);
        } else {
            // value without quotes
            while (c != eof && c != delimeter && c != newline) {
                value += c;
                c = data.charAt(++i);
            }
        }

        // add the value to the array
        if (array.length <= row)
            array.push([]);
        array[row].push(value);

        // skip whitespaces
        while (c == ' ' || c == '\t' || c == '\r') {
            c = data.charAt(++i);
        }

        // go to the next row or column
        if (c == delimeter) {
            // to the next column
            col++;
        } else if (c == newline) {
            // to the next row
            col = 0;
            row++;
        } else if (c != eof) {
            // unexpected character
            let nearby = "";
            for (let j = 0; j < 100 && (data.length - 1) > (i + j); j++) {
                nearby += data.charAt(i + j);
            }

            throw "Delimiter expected after character " + i + " found " + c + " nearby " + nearby;
        }

        // go to the next character
        c = data.charAt(++i);
    }

    return array;
}

export const ppanelImportStore = new PPanelImportStore();